import { useCallback, useEffect, useMemo } from 'react';
import type { ReactElement } from 'react';
import { readStoredTheme, type ResolvedData } from '@tallyframe/core';
import { PrintReport } from '@tallyframe/renderer';
import { useExportPayload } from '../api/public-queries';
import { ApiError } from '../api/errors';
import type { ExportPayload } from '../api/types';

declare global {
  interface Window {
    /** Raised by the renderer once fonts, data and every chart are done.
     *  Playwright waits on exactly this (contracts §5, §7). */
    __TALLYFRAME_PRINT_READY__?: boolean;
    /** Set by this route when the payload could not be loaded at all, so an
     *  export can fail fast instead of waiting out the readiness timeout. */
    __TALLYFRAME_PRINT_ERROR__?: { code: string; message: string };
  }
}

/**
 * Guards the one field `PrintRoute` dereferences unconditionally
 * (`resolved.brand.name`/`.accent`/`.logoUrl`, below).
 *
 * The export payload request — like every method on the client — casts the parsed
 * response JSON to its declared type unchecked; there is no runtime
 * validation anywhere on the client, and adding one belongs to a
 * cross-cutting task of its own, not this route. But this route is not like
 * the rest of the app: it runs unattended inside headless Chromium against a
 * hard export timeout, with no person to retry it and no `ErrorBoundary`
 * anywhere in the tree to catch a render-phase throw. A 200 with valid JSON
 * that is simply missing `brand` — an older server, or a replayed export
 * minted before this field existed — must fail through the same explicit
 * `__TALLYFRAME_PRINT_ERROR__` path as a rejected fetch, not throw mid-render
 * and hang the exporter for the full timeout with no diagnostic. Hence a
 * narrow, local shape check on exactly the field this component needs,
 * rather than a broader client-side validation change.
 *
 * `issuedAt` deliberately gets no such guard: `PrintRoute` only ever passes
 * it through by value, never dereferences into it, and `PrintReport`'s own
 * contract already treats a missing `issuedAt` as "omit the Issued line"
 * rather than an error (packages/renderer/src/types.ts). A payload without
 * an issue date is a valid, if slightly less complete, document; a payload
 * without a brand is one this route cannot render at all.
 */
function hasValidBrand(payload: ExportPayload): boolean {
  const brand = payload.brand as { name?: unknown; accent?: unknown; logoUrl?: unknown; badge?: unknown } | null | undefined;
  return (
    typeof brand === 'object' &&
    brand !== null &&
    typeof brand.name === 'string' &&
    typeof brand.accent === 'string' &&
    (typeof brand.logoUrl === 'string' || brand.logoUrl === null) &&
    typeof brand.badge === 'boolean'
  );
}

const INLINE_CODE = 'font-mono text-sm bg-desk border border-solid border-line rounded-control px-1';

/** The load failure to report, if any: a failed request, or a payload this route cannot render. */
function printError(data: ExportPayload | undefined, failure: unknown): ApiError | null {
  if (failure) {
    return ApiError.isApiError(failure)
      ? failure
      : new ApiError('EXPORT_FAILED', failure instanceof Error ? failure.message : String(failure), 0);
  }
  if (data && !hasValidBrand(data)) {
    return new ApiError(
      'EXPORT_FAILED',
      'Export payload is missing a valid brand (older server, or a replayed export?).',
      0
    );
  }
  return null;
}

/**
 * The print surface.
 *
 * Server-driven and deliberately bare: no chrome, no navigation, no session.
 * The export id is generated by the server and the payload is single-use, so
 * this page is reachable exactly once by exactly one headless browser.
 *
 * Its one obligation beyond rendering is to never stall the readiness signal.
 * A page that waits forever costs the export its whole 60-second budget and
 * then reports a timeout, which tells nobody anything. So a failed payload
 * fetch renders an explicit error *and* raises the flags, with the reason left
 * on `window.__TALLYFRAME_PRINT_ERROR__` for the export driver to inspect and
 * turn into an `EXPORT_FAILED` rather than a silently wrong PDF. A payload
 * that fetches fine but fails the `hasValidBrand` shape check above takes the
 * same path: `printError` reports it.
 */
export function PrintRoute({ exportId }: { exportId: string }): ReactElement {
  const payload = useExportPayload(exportId);
  const { data: resolved, error: failure } = payload;
  const error = useMemo(() => printError(resolved, failure), [resolved, failure]);

  useEffect(() => {
    if (!error) return;
    window.__TALLYFRAME_PRINT_ERROR__ = { code: error.code, message: error.message };
    document.body.dataset.printError = 'true';
    // Unblock the waiter. The error object above is what distinguishes a
    // real document from a page that has nothing to print.
    document.body.dataset.printReady = 'true';
    window.__TALLYFRAME_PRINT_READY__ = true;
  }, [error]);

  const onReady = useCallback(() => {
    // The renderer has already set both readiness flags by the time this fires.
    // This marks the host route as settled too, so a half-mounted page is
    // distinguishable from a finished one while debugging an export.
    document.body.dataset.printSurface = 'ready';
  }, []);

  if (error) {
    return (
      <div className="tfw-print-error">
        <h1>This report could not be printed</h1>
        <p>{error.message}</p>
        <p className="text-muted text-sm">
          Export id <code className={INLINE_CODE}>{exportId}</code> · error code{' '}
          <code className={INLINE_CODE}>{error.code}</code>
        </p>
        <p className="text-muted text-sm">
          The export payload is single-use and expires. If this id has already been rendered once,
          start a new export.
        </p>
      </div>
    );
  }

  if (!resolved) {
    // Not a spinner: a spinner that never resolves is exactly the failure this
    // route is supposed to avoid, and there is nothing to animate for a machine.
    return <div className="tfw-print-error">Loading export payload…</div>;
  }

  // Already resolved server-side; this is a lenient parse only. Never throws,
  // so a missing or corrupt theme degrades to defaults instead of failing.
  const theme = readStoredTheme(resolved.brand.theme);

  const data: ResolvedData = {
    dataset: resolved.dataset,
    snapshot: resolved.snapshot,
    rows: resolved.rows
  };

  return (
    <PrintReport
      definition={resolved.definition}
      data={data}
      brand={{
        name: resolved.brand.name,
        accent: resolved.brand.accent,
        logoUrl: resolved.brand.logoUrl,
        theme
      }}
      badge={resolved.brand.badge}
      issuedAt={resolved.issuedAt}
      onReady={onReady}
    />
  );
}
